import React, { useMemo, useState } from "react";
import PageWrapper from "components/PageWrapper";
import { Search, MapPin, Globe, Star, Settings, Plus } from "lucide-react";
import { Switch } from "components/ui/switch";

const SUPPLIER_WM = {
  mouser: "bg-[#005DAA]",
  digikey: "bg-[#CC0000]",
  element14: "bg-[#FF6600]",
  arrow: "bg-[#0B7D72]",
  tme: "bg-[#E2001A]",
  avnet: "bg-[#00457C]",
};

const SUPPLIERS = [
  { id: "mouser", short: "Mou", name: "Mouser Electronics", rating: 4.8, location: "Mansfield, TX, USA", website: "mouser.com", categories: ["Semiconductors", "Passives", "Connectors", "Electromechanical"], perf: { onTime: 98, quality: 97, comms: 94 }, connected: true },
  { id: "digikey", short: "DK", name: "Digi-Key Electronics", rating: 4.9, location: "Thief River Falls, MN, USA", website: "digikey.com", categories: ["Semiconductors", "Passives", "Connectors", "Tools"], perf: { onTime: 99, quality: 98, comms: 96 }, connected: true },
  { id: "element14", short: "E14", name: "Element 14", rating: 4.6, location: "Leeds, UK", website: "element14.com", categories: ["Semiconductors", "Passives", "Test & Measure"], perf: { onTime: 95, quality: 94, comms: 91 }, connected: true },
  { id: "arrow", short: "Arr", name: "Arrow Electronics", rating: 4.5, location: "Centennial, CO, USA", website: "arrow.com", categories: ["Semiconductors", "Passives", "Engineering services"], perf: { onTime: 92, quality: 93, comms: 89 }, connected: true },
  { id: "tme", short: "TME", name: "TME Electronic Components", rating: 4.4, location: "Łódź, Poland", website: "tme.eu", categories: ["Semiconductors", "Passives", "Tools"], perf: { onTime: 91, quality: 92, comms: 88 }, connected: false },
  { id: "avnet", short: "Av", name: "Avnet", rating: 4.3, location: "Phoenix, AZ, USA", website: "avnet.com", categories: ["Semiconductors", "Embedded systems", "Engineering"], perf: { onTime: 90, quality: 92, comms: 87 }, connected: false },
];

const SuppliersPage = () => {
  const [q, setQ] = useState("");
  const [conn, setConn] = useState(
    Object.fromEntries(SUPPLIERS.map((s) => [s.id, s.connected])),
  );

  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return SUPPLIERS;
    return SUPPLIERS.filter(
      (s) =>
        s.name.toLowerCase().includes(qq) ||
        s.location.toLowerCase().includes(qq) ||
        s.categories.some((c) => c.toLowerCase().includes(qq)),
    );
  }, [q]);

  const connectedCount = Object.values(conn).filter(Boolean).length;

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              Suppliers
            </h1>
            <span className="text-sm text-s360-fg3">
              {connectedCount} of {SUPPLIERS.length} connected · benchmark live pricing across every
              active source.
            </span>
          </div>
          <button className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600">
            <Plus className="w-3.5 h-3.5" /> Add supplier
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-2 h-9 px-3 bg-white border border-s360-stroke1 border-b-2 rounded-md min-w-[280px] flex-1 focus-within:border-s360-violet text-s360-fg3 focus-within:text-s360-fg1">
            <Search className="w-3.5 h-3.5" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, location, or category…"
              className="flex-1 bg-transparent outline-none text-sm text-s360-fg1 placeholder:text-s360-fg-disabled"
            />
          </div>
          <span className="ml-auto text-xs text-s360-fg3">
            <strong className="text-s360-fg1">{visible.length}</strong> of {SUPPLIERS.length} suppliers
          </span>
        </div>

        {/* Supplier grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((s) => {
            const isOn = !!conn[s.id];
            return (
              <div
                key={s.id}
                className={`bg-white rounded-md shadow-sm p-5 flex flex-col gap-4 border ${
                  isOn ? "border-s360-violet-100" : "border-transparent"
                }`}
              >
                {/* Head */}
                <div className="flex items-start gap-3">
                  <span
                    className={`w-11 h-11 rounded-lg inline-flex items-center justify-center text-[13px] font-bold text-white shrink-0 ${
                      SUPPLIER_WM[s.id] || "bg-s360-violet"
                    }`}
                  >
                    {s.short}
                  </span>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-base font-semibold text-s360-fg1 truncate">{s.name}</span>
                    <span className="flex items-center gap-1 text-xs">
                      <Star className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623]" />
                      <span className="text-[#F5A623] font-semibold">{s.rating}</span>
                      <span className="text-s360-fg3 ml-1">
                        · {isOn ? "API connected" : "Not connected"}
                      </span>
                    </span>
                  </div>
                  <Switch
                    checked={isOn}
                    onClick={() => setConn((p) => ({ ...p, [s.id]: !p[s.id] }))}
                    className="data-[state=checked]:bg-s360-violet data-[state=unchecked]:bg-s360-bg3"
                  />
                </div>

                {/* Info */}
                <div className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-2 text-[13px] text-s360-fg2">
                    <MapPin className="w-4 h-4 text-s360-fg3" /> {s.location}
                  </span>
                  <span className="flex items-center gap-2 text-[13px] text-s360-violet">
                    <Globe className="w-4 h-4" />
                    <a href="#" className="hover:underline">
                      {s.website}
                    </a>
                  </span>
                </div>

                {/* Categories */}
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                    Categories
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {s.categories.map((c, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-s360-bg2 text-s360-fg2 rounded-full text-xs border border-s360-stroke1"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Performance */}
                <div className="grid grid-cols-3 gap-3 text-center pt-1">
                  {[
                    { v: s.perf.onTime, l: "On time" },
                    { v: s.perf.quality, l: "Quality" },
                    { v: s.perf.comms, l: "Comms" },
                  ].map((m) => (
                    <div key={m.l} className="flex flex-col">
                      <span className="text-lg font-bold text-s360-ink tabular-nums">{m.v}%</span>
                      <span className="text-[11px] text-s360-fg3">{m.l}</span>
                    </div>
                  ))}
                </div>

                <button className="inline-flex items-center justify-center gap-1.5 w-full h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2">
                  <Settings className="w-4 h-4" /> {isOn ? "Manage API key" : "Connect API"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </PageWrapper>
  );
};

export default SuppliersPage;
